import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import classNames from 'classnames';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProject } from '@/store/slices/project/selectors';
import type { LocalCampaignTemplate } from '@/micros/edit-project/grid-areas/main/campaigns/templates';
import classes from './template-strip.module.css';

const FALLBACK_SCALE = 0.26;

interface TemplateStripCardProps {
  template: LocalCampaignTemplate;
  isSelected: boolean;
  timezone?: string;
  tabIndex?: number;
  onFocus?: () => void;
  onSelect: (templateUuid: string) => void;
}

export default function TemplateStripCard({
  template,
  isSelected,
  timezone,
  tabIndex,
  onFocus,
  onSelect,
}: TemplateStripCardProps) {
  const event = useAppSelector(getWorkingProject);
  const previewRef = useRef<HTMLDivElement>(null);
  const templateRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(FALLBACK_SCALE);
  const [offset, setOffset] = useState({ left: 0, top: 0 });

  const TemplateComponent = template.component;

  useLayoutEffect(() => {
    const node = templateRef.current;
    const preview = previewRef.current;
    if (!node || !preview) return;

    node.inert = true;

    const updateScale = () => {
      const { offsetWidth: width, offsetHeight: height } = node;
      if (!width || !height) return;
      const nextScale = Math.min(preview.clientWidth / width, preview.clientHeight / height);
      setScale(nextScale);
      setOffset({
        left: (preview.clientWidth - width * nextScale) / 2,
        top: (preview.clientHeight - height * nextScale) / 2,
      });
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(node);
    return () => observer.disconnect();
  }, [event, TemplateComponent]);

  return (
    <Button
      clear
      width="180px"
      className={classNames('flex-column gap-6 flex-shrink-0 text-left', {
        [classes.stripCardSelected]: isSelected,
      })}
      tabIndex={tabIndex}
      onFocus={onFocus}
      onClick={() => onSelect(template.uuid)}
    >
      <Container
        ref={previewRef}
        width="180px"
        height="240px"
        borderRadius="8px"
        background="var(--gray-900)"
        borderWidth="2px"
        borderStyle="solid"
        borderColor="transparent"
        transition="border-color 0.15s"
        className={classNames('position-relative overflow-hidden', classes.stripCardPreview)}
        style={{ whiteSpace: 'normal' }}
      >
        {event && (
          <Container
            ref={templateRef}
            width="max-content"
            className={classNames('position-absolute', classes.scaledTemplate)}
            style={{ '--strip-card-scale': scale, left: offset.left, top: offset.top } as CSSProperties}
          >
            <TemplateComponent event={event} timezone={timezone} />
          </Container>
        )}
      </Container>
      <Text className="H6" clamp={1} design="GlobalIcoText1">
        {template.title}
      </Text>
    </Button>
  );
}
